import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject, EventEmitter, Output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { BlockUIModule } from 'primeng/blockui';
import { FormatClaimPipe } from '../../../pipes/format-claim.pipe';
import { ViewService } from '../../../services/view/view.service';
import { WatchButtonComponent } from '../buttons/watch-button.component';
import { LinkLabelComponent } from './link-label.component';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-claim-label',
  imports: [
    RouterModule,
    WatchButtonComponent,
    BlockUIModule,
    CommonModule,
    FormatClaimPipe,
    LinkLabelComponent,
  ],
  providers: [],
  template: `
    @if (claim) {
      <div class="tw-flex tw-items-center tw-min-w-0">
        @if (!blocked) {
          @if (readOnly) {
            <div class="tw-flex tw-items-center tw-min-w-0">
              <span class="tw-text-[12px] md:tw-text-[14px]">{{
                fileNumber | formatClaim: fileNumFormat
              }}</span>
            </div>
          } @else {
            <app-link-label
              (click)="navigateClaim()"
              [ariaLabel]="'Open Claim ' + fileNumber"
              classes="tw-text-[12px] md:tw-text-[14px]"
            >
              {{ fileNumber | formatClaim: fileNumFormat }}
            </app-link-label>
          }
          @if (showWatchButton) {
            <app-watch-button
              [recordSource]="recordSource"
              [recordId]="recordId"
            />
          }
        } @else {
          <div class="tw-w-full tw-text-[var(--sed-black-60-color)]">
            <i>Loading...</i>
          </div>
        }
      </div>
    }
  `,
  styles: ``,
})
export class ClaimLabelComponent implements OnInit {
  private viewService = inject(ViewService);

  @Input({ required: true })
  claim: any;

  @Input()
  showWatchButton = true;

  /**
   * When true, hyperlink is disabled.
   */
  @Input()
  readOnly = false;

  /**
   * Name of fileNumber field on dataSource.
   */
  @Input()
  fileNumberName = 'fileNumber';

  /**
   * Name of fileNumFormat field on dataSource.
   */
  @Input()
  fileNumFormatName = 'fileNumFormat';

  /**
   * Name of recordSource field on dataSource.
   */
  @Input()
  recordSourceName = 'recordSource';

  /**
   * Name of recordId field on dataSource.
   */
  @Input()
  recordIdName = 'recordId';

  /**
   * fileNumber value. Overrides dataSource.
   */
  @Input()
  fileNumber: string;

  /**
   * fileNumFormat value. Overrides dataSource.
   */
  @Input()
  fileNumFormat: string;

  /**
   * recordSource value. Overrides dataSource.
   */
  @Input()
  recordSource: string;

  /**
   * recordId value. Overrides dataSource.
   */
  @Input()
  recordId: string;

  /**
   * Sensitive-access message for JURIS related claims the user cannot view.
   * When set, navigation is suppressed and the message is emitted via
   * sensitiveBlocked so a parent can show a dialog (BRD 057292 §3.7).
   */
  @Input()
  sensitiveAccessStatus: string | null = null;

  /**
   * Emitted instead of navigation when sensitiveAccessStatus is set.
   */
  @Output()
  sensitiveBlocked = new EventEmitter<string>();

  protected blocked = false;

  ngOnInit(): void {
    this.fileNumber ??= this.claim[this.fileNumberName];
    this.recordSource ??= this.claim[this.recordSourceName];
    this.recordId ??= this.claim[this.recordIdName];

    if (this.fileNumFormatName) {
      this.fileNumFormat ??= this.claim[this.fileNumFormatName];
    }
  }

  @HandleLoadingState({ loadingProperty: 'blocked' })
  async navigateClaim() {
    if (this.sensitiveAccessStatus) {
      this.sensitiveBlocked.emit(this.sensitiveAccessStatus);
      return;
    }
    const claimRow: any = {
      recordId: this.recordId,
      recordSource: this.recordSource,
    };
    await this.viewService.navigateClaim(claimRow);
  }
}
